import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  getObjectId,
  ServiceEventTicketPolicy,
  serviceEventTicketPolicySelector,
  useGetServiceEventTicketPolicyQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  ServiceEventTicketPolicyForm,
  ServiceEventTicketPolicyFormValues
} from "../../components/form/ServiceEventTicketPolicyForm/ServiceEventTicketPolicyForm";
import {useServiceEventTicketPolicyMutation} from "../../hooks/useServiceEventTicketPolicyMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {
  toFormDefaultsFromServiceEventTicketPolicy
} from "../../components/form/ServiceEventTicketPolicyForm/useServiceEventTicketPolicyForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<ServiceEventTicketPolicyFormValues>
}

export const ServiceEventTicketPolicyFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <ServiceEventTicketPolicyFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const ServiceEventTicketPolicyFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectServiceEventTicketPolicy = useMemo(() => {
    return serviceEventTicketPolicySelector()
  }, [])

  const {
    currServiceEventTicketPolicy,
    isLoading
  } = useGetServiceEventTicketPolicyQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currServiceEventTicketPolicy: selectServiceEventTicketPolicy(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<ServiceEventTicketPolicyFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromServiceEventTicketPolicy(currServiceEventTicketPolicy);
  }, [currServiceEventTicketPolicy, formDefaults]);

  const isEdit = useMemo(() => {
    return currServiceEventTicketPolicy && !copy
  }, [currServiceEventTicketPolicy, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.serviceeventticketpolicy.form.title.edit", "Edit Service Event Ticket Policy", {ns: "common"});
    } else {
      return t("page.entity.serviceeventticketpolicy.form.title.new", "New Service Event Ticket Policy", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createServiceEventTicketPolicy,
    updateServiceEventTicketPolicy,
    isCreating,
    isUpdating,
  } = useServiceEventTicketPolicyMutation()

  const onServiceEventTicketPolicySubmit = useCallback((formValues: ServiceEventTicketPolicyFormValues) => {
    const patch: Partial<ServiceEventTicketPolicy> = {
      name: formValues.name,
      description: formValues.description,
      eventType: formValues.eventType.code,
      companyId: getObjectId(formValues.company),
      serviceTypeId: getObjectId(formValues.serviceType),
      triggerFacet: formValues.triggerFacet,
      triggerFromStatus: formValues.triggerFromStatus,
      triggerToStatus: formValues.triggerToStatus,
      minSeverity: formValues.minSeverity,
      category: formValues.category.code,
      freightTypeId: getObjectId(formValues.freightType),
      defaultQuantity: formValues.defaultQuantity?.number,
      defaultQuantityUom: formValues.defaultQuantity?.units,
      priority: formValues.priority,
      doNotUse: formValues.doNotUse,
    }

    if (isEdit) {
      const policy: Partial<ServiceEventTicketPolicy> = {
        ...currServiceEventTicketPolicy.asTypeValue(),
        ...patch,
      };
      updateServiceEventTicketPolicy(policy, {onSuccess: (payload) => navigate(`/service-event-ticket-policies/profile?id=${payload.id}`, {replace: true})})
    } else {
      const policy: Partial<ServiceEventTicketPolicy> = {
        ...patch,
      };
      createServiceEventTicketPolicy(policy, {onSuccess: (payload) => navigate(`/service-event-ticket-policies/profile?id=${payload.id}`, {replace: true})})
    }
  }, [currServiceEventTicketPolicy, isEdit, updateServiceEventTicketPolicy, navigate, createServiceEventTicketPolicy]);

  return (
    <ProtectedPage
      entity={currServiceEventTicketPolicy}
      permissions={[isEdit ? "serviceeventticketpolicy_edit" : "serviceeventticketpolicy_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <ServiceEventTicketPolicyForm
                  options={{
                    regionId: regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onServiceEventTicketPolicySubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

